'use client';

import type { ReactNode } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { Icon, Tabs, Tooltip } from '@terra/ui-design-system';
import { HelpCircleIcon } from '@terra/ui-icons';

export type SettingsSubTabItem<TValue extends string> = {
  value: TValue;
  label: string;
  tooltip?: string;
};

type SettingsSubTabsProps<TValue extends string> = {
  value: TValue;
  onValueChange: (value: TValue) => void;
  items: ReadonlyArray<SettingsSubTabItem<TValue>>;
  actions?: ReactNode;
  children: ReactNode;
};

export function SettingsSubTabs<TValue extends string>({
  value,
  onValueChange,
  items,
  actions,
  children,
}: SettingsSubTabsProps<TValue>) {
  return (
    <Tabs.Root
      value={value}
      onValueChange={(details) => {
        const next = items.find((item) => item.value === details.value);

        if (next) {
          onValueChange(next.value);
        }
      }}
      size="sm"
      lazyMount
      unmountOnExit
    >
      <Tabs.List>
        {items.map((item) => (
          <Tabs.Trigger key={item.value} value={item.value}>
            <HStack gap={1} alignItems="center">
              {item.label}
              {item.tooltip ? (
                <Tooltip title={item.tooltip}>
                  <Icon asChild color="fg.muted" width={4} height={4}>
                    <HelpCircleIcon />
                  </Icon>
                </Tooltip>
              ) : null}
            </HStack>
          </Tabs.Trigger>
        ))}
        {actions ? (
          <HStack flex={1} justifyContent="flex-end" pb={1}>
            {actions}
          </HStack>
        ) : null}
        <Tabs.Indicator />
      </Tabs.List>
      {children}
    </Tabs.Root>
  );
}
